// =============================================================================
// Collapsible-content a11y — keep aria-expanded in sync with Motto's `active` class.
//
// Motto's vendored assets/collapsible-content.js (NOT edited — house rule: no vendored-JS edits)
// toggles the `active` class on `.collapsible-content__item` but never updates aria-expanded on the
// toggle button. This module observes each item's class attribute and mirrors `active` →
// aria-expanded on its `.collapsible-content__toggle`, so assistive tech announces the
// expanded/collapsed state correctly. It ALSO ENFORCES single-open: Motto's vendored JS tries to
// remove `active` from the previously-open item via jQuery `.siblings()`, but `content_for 'blocks'`
// wraps each item in a `.shopify-block`, so the items are NOT siblings and that reset silently fails —
// the closed items' +/- glyph (CSS-driven off `.active`) then sticks in the open (−) state. This
// module strips `active` from the OTHER items when one opens, resetting their glyph + aria.
//
// The Liquid sets the INITIAL aria-expanded from default_open; this module reconciles it once on
// bind and then keeps it in sync after every JS-driven toggle. Dependency-free (no jQuery); bundled
// into assets/global.autojs.js via entry/global.js → objects/global/index.js. Re-runs on
// shopify:section:load (the theme editor replaces the section wrapper, recreating the items).
// =============================================================================

const ITEM_SELECTOR = '.collapsible-content__item';
const TOGGLE_SELECTOR = '.collapsible-content__toggle';
const CONTAINER_SELECTOR = '.collapsible-content__items';

function syncItem(item) {
  // The toggle is a DIRECT child of the item (Motto DOM contract) — match only that one.
  const toggle = item.querySelector(':scope > ' + TOGGLE_SELECTOR);
  if (!toggle) return;
  toggle.setAttribute(
    'aria-expanded',
    item.classList.contains('active') ? 'true' : 'false'
  );
}

function bindContainer(container) {
  if (container.dataset.ccA11yBound === 'true') return; // idempotent across re-inits
  container.dataset.ccA11yBound = 'true';

  const items = container.querySelectorAll(ITEM_SELECTOR);
  items.forEach(syncItem); // reconcile initial state with the rendered class

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        const item = mutation.target;
        syncItem(item);
        // Single-open glyph reset (see header): when an item becomes active, strip `active` from every
        // OTHER item in the container — Motto's `.siblings()` reset fails across the `.shopify-block`
        // wrappers. Each removal re-fires this observer → syncItem(other) → aria-expanded=false + glyph
        // back to `+`. No loop: removing `active` never re-adds it.
        if (item.classList.contains('active')) {
          container.querySelectorAll(ITEM_SELECTOR).forEach((other) => {
            if (other !== item && other.classList.contains('active')) {
              other.classList.remove('active');
            }
          });
        }
      }
    });
  });
  items.forEach((item) =>
    observer.observe(item, { attributes: true, attributeFilter: ['class'] })
  );
}

function initCollapsibleContentA11y() {
  document.querySelectorAll(CONTAINER_SELECTOR).forEach(bindContainer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollapsibleContentA11y);
} else {
  initCollapsibleContentA11y();
}
document.addEventListener('shopify:section:load', initCollapsibleContentA11y);

export default initCollapsibleContentA11y;
